import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { APP_NAME } from "../config/app";
import { isAppleAuthEnabled, isGoogleAuthEnabled } from "../config/env";
import { useAuth } from "../app/AuthProvider";
import { Button } from "../components/Button";
import { InputField } from "../components/Field";
import { Tabs, type TabItem } from "../components/Tabs";
import { useI18n } from "../i18n/I18nProvider";
import { ApiRequestError } from "../services/api";
import { completeInvitation, confirmPasswordReset, getOidcStartUrl, requestPasswordReset, validateInvitation } from "../services/auth";
import { getPublicConfig } from "../services/config";
import { getValidationFieldErrors } from "../lib/apiValidation";
import { isWebAuthnSupported } from "../lib/webauthn";
import { isOnboardingComplete } from "../routes/guards";

type AuthTab = "login" | "register" | "reset" | "invite";

export function AuthPage() {
  const { t, translateError } = useI18n();
  const { login, loginWithPasskey, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isInviteRoute = location.pathname === "/auth/invite";
  const initialToken = searchParams.get("token") ?? "";
  const [pending, setPending] = useState(false);
  const [tab, setTab] = useState<AuthTab>(isInviteRoute ? "invite" : initialToken ? "reset" : "login");
  const [publicSignupEnabled, setPublicSignupEnabled] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [formError, setFormError] = useState("");
  const [formTone, setFormTone] = useState<"error" | "success">("error");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [postAuthPath, setPostAuthPath] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState(isInviteRoute ? "" : initialToken);
  const [inviteToken, setInviteToken] = useState(isInviteRoute ? initialToken : "");
  const [inviteEmail, setInviteEmail] = useState("");
  const googleAuthEnabled = isGoogleAuthEnabled();
  const appleAuthEnabled = isAppleAuthEnabled();
  const passkeySupported = isWebAuthnSupported();
  const isMfaStep = tab === "login" && needsTotp;

  const returnPath = useMemo(() => {
    const state = location.state as { from?: string } | null;
    return state?.from && !state.from.startsWith("/auth") ? state.from : "/today";
  }, [location.state]);

  useEffect(() => {
    if (isInviteRoute) {
      setTab("invite");
      setInviteToken(initialToken);
      return;
    }
    setResetToken(initialToken);
    if (initialToken) {
      setTab("reset");
    }
  }, [initialToken, isInviteRoute]);

  useEffect(() => {
    let active = true;
    getPublicConfig()
      .then((config) => {
        if (!active) {
          return;
        }
        const enabled = config.public_signup_enabled !== false;
        setPublicSignupEnabled(enabled);
        if (!enabled && tab === "register") {
          setTab("login");
          setFormTone("error");
          setFormError(t("auth.signupDisabled"));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [t, tab]);

  useEffect(() => {
    if (tab !== "invite" || !inviteToken) {
      return;
    }
    let active = true;
    validateInvitation(inviteToken)
      .then((invitation) => {
        if (!active) {
          return;
        }
        setInviteEmail(invitation.email ?? "");
        setFormError("");
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setInviteEmail("");
        handleApiError(error, translateError, t, setFormError, setFormTone, setFieldErrors);
      });
    return () => {
      active = false;
    };
  }, [inviteToken, tab, t, translateError]);

  useEffect(() => {
    if (user) {
      void navigate(isOnboardingComplete(user) ? postAuthPath ?? returnPath : "/onboarding", { replace: true });
    }
  }, [navigate, postAuthPath, returnPath, user]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    setPending(true);
    try {
      if (tab === "login") {
        setPostAuthPath(returnPath);
        const nextUser = await login(email, password, needsTotp ? totpCode : undefined);
        if (!nextUser) {
          setPostAuthPath(null);
          setFormError(t("auth.sessionMissing"));
        }
        return;
      }
      if (tab === "register") {
        if (!publicSignupEnabled) {
          setFormError(t("auth.signupDisabled"));
          return;
        }
        setPostAuthPath("/onboarding");
        const nextUser = await register(email, password, displayName);
        if (!nextUser) {
          setPostAuthPath(null);
          setTab("login");
          setPassword("");
          setFormTone("success");
          setFormError(t("auth.registerSuccessLogin"));
        }
        return;
      }
      if (tab === "invite") {
        if (!inviteToken) {
          setFormError(translateError("INVITATION_INVALID"));
          return;
        }
        if (!passwordsMatch()) {
          return;
        }
        await completeInvitation(inviteToken, {
          password: password,
          password_confirm: confirmPassword
        });
        setTab("login");
        setPassword("");
        setConfirmPassword("");
        setFormTone("success");
        setFormError(t("auth.invitationComplete"));
        void navigate("/auth", { replace: true });
        return;
      }
      if (resetToken) {
        if (!passwordsMatch()) {
          return;
        }
        await confirmPasswordReset({ token: resetToken, new_password: password, new_password_confirm: confirmPassword });
        setTab("login");
        setPassword("");
        setConfirmPassword("");
        setFormTone("success");
        setFormError(t("auth.resetSuccess"));
      } else {
        await requestPasswordReset({ email });
        setFormTone("success");
        setFormError(t("auth.resetSent"));
      }
    } catch (error) {
      if (tab === "login" && error instanceof ApiRequestError && error.code === "AUTH_MFA_REQUIRED") {
        setNeedsTotp(true);
        setTotpCode("");
        setFieldErrors({});
        setFormError("");
        return;
      }
      handleApiError(error, translateError, t, setFormError, setFormTone, setFieldErrors);
    } finally {
      setPending(false);
    }
  };

  const handlePasskeyLogin = async () => {
    resetFeedback();
    setPending(true);
    try {
      setPostAuthPath(returnPath);
      const nextUser = await loginWithPasskey();
      if (!nextUser) {
        setPostAuthPath(null);
        setFormError(t("auth.sessionMissing"));
      }
    } catch (error) {
      handleApiError(error, translateError, t, setFormError, setFormTone, setFieldErrors);
    } finally {
      setPending(false);
    }
  };

  const resetFeedback = () => {
    setFormError("");
    setFormTone("error");
    setFieldErrors({});
  };

  const passwordsMatch = () => {
    if (password !== confirmPassword) {
      setFieldErrors({ new_password_confirm: t("auth.passwordsMismatch"), password_confirm: t("auth.passwordsMismatch") });
      setFormError(t("auth.passwordsMismatch"));
      return false;
    }
    return true;
  };

  const authTabs: TabItem<AuthTab>[] = [
    { value: "login", label: t("auth.login") },
    ...(publicSignupEnabled ? [{ value: "register" as const, label: t("auth.register") }] : []),
    { value: "reset", label: t("auth.forgotPassword") }
  ];

  return (
    <main className="auth-screen">
      <section className="auth-panel panel">
        <div className="stack">
          <div>
            <p className="page-header__eyebrow">{APP_NAME}</p>
            <h1>{headingFor(tab, t, isMfaStep)}</h1>
          </div>
          {tab !== "invite" && !isMfaStep ? (
            <Tabs
              value={tab}
              onChange={(nextTab) => {
                setTab(nextTab);
                resetFeedback();
                setNeedsTotp(false);
                setTotpCode("");
                setPassword("");
                setConfirmPassword("");
              }}
              items={authTabs}
            />
          ) : null}
          {!publicSignupEnabled && tab !== "invite" ? <p className="field__hint">{t("auth.signupDisabled")}</p> : null}
          <form className="form-grid" onSubmit={(event) => void submit(event)}>
            {formError ? (
              <div className={`form-alert ${formTone === "success" ? "form-alert--success" : ""}`} role={formTone === "success" ? "status" : "alert"}>
                {formError}
              </div>
            ) : null}
            {tab === "invite" && inviteEmail ? <p className="field__hint">{t("auth.invitationFor").replace("{email}", inviteEmail)}</p> : null}
            {isMfaStep ? <p className="field__hint">{t("auth.mfaPrompt")}</p> : null}
            {needsEmail(tab, resetToken, isMfaStep) ? (
              <InputField
                label={t("auth.email")}
                type="email"
                autoComplete="email"
                required
                value={email}
                error={fieldErrors.email}
                onChange={(event) => setEmail(event.target.value)}
              />
            ) : null}
            {tab === "register" ? (
              <InputField
                label={t("auth.displayName")}
                autoComplete="name"
                value={displayName}
                error={fieldErrors.display_name}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            ) : null}
            {(tab === "login" && !isMfaStep) || tab === "register" ? (
              <InputField
                label={t("auth.password")}
                type="password"
                autoComplete={tab === "login" ? "current-password" : "new-password"}
                required
                minLength={tab === "register" ? 12 : undefined}
                value={password}
                error={fieldErrors.password}
                onChange={(event) => setPassword(event.target.value)}
              />
            ) : null}
            {isMfaStep ? (
              <InputField
                label={t("auth.totpCode")}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
                value={totpCode}
                error={fieldErrors.totp_code}
                onChange={(event) => setTotpCode(event.target.value)}
              />
            ) : null}
            {isMfaStep ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setNeedsTotp(false);
                  setTotpCode("");
                  resetFeedback();
                }}
              >
                {t("auth.useDifferentLogin")}
              </Button>
            ) : null}
            {(tab === "reset" && resetToken) || tab === "invite" ? (
              <>
                <InputField
                  label={t("auth.newPassword")}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  error={fieldErrors.new_password ?? fieldErrors.password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <InputField
                  label={t("auth.repeatNewPassword")}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={confirmPassword}
                  error={fieldErrors.new_password_confirm ?? fieldErrors.password_confirm}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </>
            ) : null}
            <Button type="submit" disabled={pending || (tab === "register" && !publicSignupEnabled)}>
              {buttonLabelFor(tab, resetToken, t, isMfaStep)}
            </Button>
          </form>
          {tab === "login" && !isMfaStep && passkeySupported ? (
            <Button variant="secondary" onClick={() => void handlePasskeyLogin()} disabled={pending}>
              {t("auth.passkeyLogin")}
            </Button>
          ) : null}
          {tab === "login" && !isMfaStep && (googleAuthEnabled || appleAuthEnabled) ? (
            <div className="grid-two">
              {googleAuthEnabled ? (
                <Button variant="secondary" onClick={() => window.location.assign(getOidcStartUrl("google"))}>
                  {t("auth.google")}
                </Button>
              ) : null}
              {appleAuthEnabled ? (
                <Button variant="secondary" onClick={() => window.location.assign(getOidcStartUrl("apple"))}>
                  {t("auth.apple")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function needsEmail(tab: AuthTab, resetToken: string, isMfaStep: boolean): boolean {
  return !isMfaStep && (tab === "login" || tab === "register" || (tab === "reset" && !resetToken));
}

function headingFor(tab: AuthTab, t: (key: string) => string, isMfaStep: boolean): string {
  if (isMfaStep) {
    return t("auth.mfaTitle");
  }
  if (tab === "register") {
    return t("auth.register");
  }
  if (tab === "reset") {
    return t("auth.forgotPassword");
  }
  if (tab === "invite") {
    return t("auth.invitationTitle");
  }
  return t("auth.login");
}

function buttonLabelFor(tab: AuthTab, resetToken: string, t: (key: string) => string, isMfaStep: boolean): string {
  if (isMfaStep) {
    return t("auth.verifyContinue");
  }
  if (tab === "login") {
    return t("auth.login");
  }
  if (tab === "register") {
    return t("auth.register");
  }
  if (tab === "invite") {
    return t("auth.invitationSubmit");
  }
  return resetToken ? t("auth.resetConfirm") : t("auth.forgotPassword");
}

function handleApiError(
  error: unknown,
  translateError: (code: string) => string,
  t: (key: string) => string,
  setFormError: (value: string) => void,
  setFormTone: (value: "error" | "success") => void,
  setFieldErrors: (value: Record<string, string>) => void
): void {
  if (error instanceof ApiRequestError) {
    setFieldErrors(getValidationFieldErrors(error.details, t("auth.fieldInvalid")));
    const requestSuffix = error.requestId ? ` (${t("common.requestId")}: ${error.requestId})` : "";
    setFormTone("error");
    setFormError(`${translateError(error.code)}${requestSuffix}`);
  } else if (error instanceof Error && error.message === "PASSKEY_UNSUPPORTED") {
    setFormTone("error");
    setFormError(t("auth.passkeyUnsupported"));
  } else if (error instanceof Error && error.message === "PASSKEY_CANCELLED") {
    setFormTone("error");
    setFormError(t("auth.passkeyCancelled"));
  } else {
    setFormTone("error");
    setFormError(translateError("INTERNAL_ERROR"));
  }
}
